import { Button } from "@/components/ui/button.tsx";
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Apple, Smartphone } from 'lucide-react';
import phoneRegBg from "@/assets/phone-registration-bg.jpg";

export default function Download() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      className="py-24 md:py-40 px-4 bg-cover bg-center relative overflow-hidden"
      id="download-section"
      ref={ref}
      style={{
        backgroundImage: `url(${phoneRegBg})`,
        backgroundColor: `rgb(71, 107, 252)`,
      }}
    >
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/80 to-blue-700/90" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 -left-20 w-96 h-96 rounded-full bg-white/10 blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </div>

      {/* Wave at top */}
      <div className="wave-divider-top">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0,64L48,69.3C96,75,192,85,288,90.7C384,96,480,96,576,85.3C672,75,768,53,864,48C960,43,1056,53,1152,64C1248,75,1344,85,1392,90.7L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            fill="white"
          />
        </svg>
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.span
            className="inline-block text-white/80 font-semibold text-sm uppercase tracking-wider mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Ke stažení
          </motion.span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
            Stáhněte si Swimmie
          </h2>

          <p className="text-white/90 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Swimmie vyžaduje iOS 16 a vyšší nebo Android 12 a vyšší.
            <br />
            Prosím, pro stažení pokračujte na svůj oblíbený store.
          </p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <a href="https://apps.apple.com/cz/app/swimmie/id6748400794" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto group bg-white hover:bg-gray-50 text-foreground font-semibold px-8 py-7 text-base rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <Apple className="mr-3 h-6 w-6 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <span className="block text-xs text-foreground/60">Stáhnout pro</span>
                  <span className="block font-bold">iOS</span>
                </div>
              </Button>
            </a>

            <a href="https://play.google.com/store/apps/details?id=com.swimmie.swimmieApp" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto group bg-white hover:bg-gray-50 text-foreground font-semibold px-8 py-7 text-base rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <Smartphone className="mr-3 h-6 w-6 group-hover:scale-110 transition-transform" />
                <div className="text-left">
                  <span className="block text-xs text-foreground/60">Stáhnout pro</span>
                  <span className="block font-bold">Android</span>
                </div>
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Wave at bottom */}
      <div className="wave-divider">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            fill="rgb(71, 107, 252)"
          />
        </svg>
      </div>
    </section>
  );
}
